/**
 * AI Automation API Client with Encryption/Decryption and Token Verification
 * 
 * All requests to ai_automation backend are encrypted using REACT_APP_SECRETKEY
 * Token verification includes uid and cid for authentication
 */

import CryptoJS from 'crypto-js';
import { getAppSecretKey, getAuthToken, getMetadata } from './encryptedStorage';

const SECRET_KEY = getAppSecretKey();
const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL;
const PUBLIC_PLAIN_ENDPOINT_PREFIXES = [
  '/ai-interview/',
  '/interviews/ai-launch/',
];

const resolveAiAutomationBaseUrl = () => {
  const configuredUrl = (AI_AUTOMATION_URL || '').trim();
  if (!configuredUrl) {
    throw new Error('REACT_APP_AI_AUTOMATION_URL is not configured.');
  }
  return configuredUrl.replace(/\/+$/, '');
};

const isPublicPlainEndpoint = (endpoint = '') => {
  const normalized = String(endpoint || '');
  return PUBLIC_PLAIN_ENDPOINT_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

const detailToMessage = (detail) => {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (item && typeof item === 'object' ? (item.msg || JSON.stringify(item)) : String(item)))
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
};

const plainJsonRequest = async (url, options = {}) => {
  let body = options.body;
  if (body && typeof body !== 'string') {
    body = JSON.stringify(body);
  }
  const callerHeaders = { ...(options.headers || {}) };
  delete callerHeaders.Authorization;
  delete callerHeaders.authorization;
  delete callerHeaders.token;
  delete callerHeaders.Token;
  delete callerHeaders['X-Encrypted'];
  delete callerHeaders['x-encrypted'];
  delete callerHeaders['X-Verification-Token'];
  delete callerHeaders['x-verification-token'];

  const timeoutMs = options.timeout === 0 ? 0 : (options.timeout || 60000);
  const controller = new AbortController();
  const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let response;
  try {
    response = await fetch(url, {
      ...options,
      method: options.method || 'GET',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...callerHeaders,
      },
      body,
      signal: controller.signal,
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  const responseText = await response.text();
  let responseData = null;
  if (responseText) {
    try {
      responseData = JSON.parse(responseText);
    } catch {
      responseData = responseText;
    }
  }

  if (!response.ok) {
    const detail = responseData && typeof responseData === 'object'
      ? detailToMessage(responseData.detail) || responseData.message
      : String(responseData || '');
    const err = new Error(detail || `Request failed with status ${response.status}`);
    err.status = response.status;
    err.raw = responseText;
    throw err;
  }

  return responseData;
};

/**
 * Encrypt data using CryptoJS AES (compatible with backend cryptojs.py)
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} Base64 encrypted string
 */
export const encryptData = (data) => {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data using CryptoJS AES
 * @param {string} encryptedData - Base64 encrypted string
 * @returns {any} Decrypted data (parsed JSON)
 */
export const decryptData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decrypted) {
      throw new Error('Decryption resulted in empty string');
    }
    
    // Try to parse as JSON, if fails return as string
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  } catch (error) {
    throw new Error('Failed to decrypt data');
  }
};

const tryDecryptData = (encryptedData) => {
  try {
    return {
      ok: true,
      data: decryptData(encryptedData),
    };
  } catch (error) {
    return {
      ok: false,
      error,
    };
  }
};

/**
 * Create verification token containing uid and cid
 * @returns {string|null} Base64 encoded token with uid and cid, or null if not available
 */
export const createVerificationToken = () => {
  try {
    const metadata = getMetadata();
    if (!metadata) {
      // Try to get from localStorage directly as fallback
      try {
        const metadataStr = localStorage.getItem('metadata');
        if (metadataStr) {
          const parsed = JSON.parse(metadataStr);
          if (parsed) {
            const uid = parsed.uid || parsed.userId || parsed.id || null;
            const cid = parsed.cid || null;
            
            if (uid) {
              const tokenPayload = {
                uid: String(uid),
                ...(cid ? { cid: String(cid) } : {}),
                timestamp: Date.now(),
              };
              const tokenString = JSON.stringify(tokenPayload);
              return encryptData(tokenString);
            }
          }
        }
      } catch (e) {
        // Ignore
      }
      console.warn('No metadata found, verification token will not be sent');
      return null;
    }
    
    const uid = metadata.uid || metadata.userId || metadata.id || null;
    const cid = metadata.cid || null;
    
    if (!uid) {
      console.warn('Missing uid in metadata, verification token will not be sent');
      return null;
    }

    // Create token payload (cid optional — admin users may not have one)
    const tokenPayload = {
      uid: String(uid),
      ...(cid ? { cid: String(cid) } : {}),
      timestamp: Date.now(),
    };
    
    // Encrypt the token payload
    const tokenString = JSON.stringify(tokenPayload);
    const encryptedToken = encryptData(tokenString);
    
    return encryptedToken;
  } catch (error) {
    console.warn('Failed to create verification token:', error);
    return null; // Return null instead of throwing, so request can still proceed with auth token
  }
};

/**
 * Make encrypted API request to ai_automation backend
 * @param {string} endpoint - API endpoint (e.g., '/sessions')
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<any>} Decrypted response data
 */
export const aiAutomationRequest = async (endpoint, options = {}) => {
  const normalizedBase = resolveAiAutomationBaseUrl();
  const url = `${normalizedBase}${endpoint}`;

  if (isPublicPlainEndpoint(endpoint)) {
    return plainJsonRequest(url, options);
  }

  try {
    // Create verification token (optional - may be null if metadata not available)
    const verificationToken = createVerificationToken();

    // Prepare request body
    let requestBody = null;
    if (options.body) {
      // Encrypt the request body
      const bodyData = typeof options.body === 'string' 
        ? JSON.parse(options.body) 
        : options.body;
      const encryptedBody = encryptData(bodyData);
      requestBody = JSON.stringify({ encrypted_data: encryptedBody });
    }

    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Encrypted': '1', // Signal to backend that request is encrypted
    };
    
    // Add verification token if available
    if (verificationToken) {
      headers['X-Verification-Token'] = verificationToken;
    }

    // Always add auth token if available (required by auth_required_middleware)
    // Try multiple sources to find the token
    const authToken = getAuthToken();
    
    // Add token to headers if found
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
      headers.token = authToken; // legacy compat
    } else {
      // If no token found, log warning but continue (verification token might be enough)
      console.warn('No auth token found in localStorage. Verification token will be used for authentication.');
    }

    // Make the request with a timeout to prevent hanging when backend is unresponsive
    // timeout: 0 means no timeout (infinite wait), useful for long-running requests like sourcing
    const timeoutMs = options.timeout === 0 ? 0 : (options.timeout || 60000); // 60s default, 0 = no timeout
    const controller = new AbortController();
    const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

    let response;
    try {
      response = await fetch(url, {
        ...options,
        method: options.method || 'GET',
        headers: {
          ...headers,
          ...(options.headers || {}),
        },
        body: requestBody,
        signal: controller.signal,
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    // Check response status
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        const parsed = JSON.parse(errorText);
        if (parsed.encrypted_data) {
          const decrypted = tryDecryptData(parsed.encrypted_data);
          if (decrypted.ok) {
            errorData = decrypted.data;
          } else {
            errorData = {
              detail: response.status === 401
                ? 'Not authenticated with the AI automation service'
                : response.status === 403
                  ? 'Access to the AI automation service was denied'
                  : response.status === 504
                    ? 'The AI automation service timed out before responding. The task may still be running in the background.'
                    : response.status === 502
                      ? 'The AI automation service is temporarily unavailable (bad gateway). Please try again.'
                      : response.status === 503
                        ? 'The AI automation service is temporarily unavailable. Please try again.'
                  : response.status === 500
                    ? 'The AI automation service returned an internal server error'
                    : `Request failed with status ${response.status}`,
            };
          }
        } else {
          errorData = parsed;
        }
      } catch {
        errorData = {
          detail:
            response.status === 504
              ? 'The AI automation service timed out before responding. The task may still be running in the background.'
              : response.status === 502
                ? 'The AI automation service is temporarily unavailable (bad gateway). Please try again.'
                : response.status === 503
                  ? 'The AI automation service is temporarily unavailable. Please try again.'
                  : errorText || `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      // Handle Pydantic validation errors (array of objects)
      let errorMessage;
      if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail
          .map(e => e.msg || JSON.stringify(e))
          .join('; ');
      } else {
        errorMessage = errorData?.detail || errorData?.message || `Request failed with status ${response.status}`;
      }
      const err = new Error(errorMessage);
      err.status = response.status;
      err.raw = errorText;
      throw err;
    }

    // Get response text
    const responseText = await response.text();
    
    if (!responseText) {
      return null;
    }

    // Try to parse response
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch {
      // If not JSON, return as is
      return responseText;
    }

    // Check if response is encrypted
    if (responseData.encrypted_data) {
      const decrypted = tryDecryptData(responseData.encrypted_data);
      if (!decrypted.ok) {
        const err = new Error('Received unreadable encrypted response from the AI automation service');
        err.status = response.status;
        throw err;
      }
      return decrypted.data;
    }

    // If not encrypted, return as is (for backward compatibility)
    return responseData;
  } catch (error) {
    const errorMsg = error?.message || String(error);
    const isNetworkError = errorMsg === 'Failed to fetch' || errorMsg.includes('NetworkError') || errorMsg.includes('ERR_CONNECTION_REFUSED') || error.name === 'AbortError';
    const isSessionNotFoundError = errorMsg.includes('Session not found') || errorMsg.includes('not found');
    const isSessionLogsEndpoint = endpoint.includes('session-logs');

    // Network errors (server down, connection refused, timeout) — show user-friendly alert
    if (isNetworkError) {
      const isTimeout = error.name === 'AbortError';
      const userMessage = isTimeout
        ? 'Request timed out. The server may be busy — please try again.'
        : 'Unable to reach the server. Please check if the backend is running and try again.';

      // Show a non-blocking toast/alert (only once per 10 seconds to avoid spam)
      const now = Date.now();
      const lastShown = window.__lastNetworkErrorToast || 0;
      if (now - lastShown > 10000) {
        window.__lastNetworkErrorToast = now;
        // Show inline toast notification (works from any page, no React dependency)
        console.warn(`[AI Automation] ${userMessage}`);
        try {
          const existing = document.getElementById('ai-server-error-toast');
          if (existing) existing.remove();
          const toast = document.createElement('div');
          toast.id = 'ai-server-error-toast';
          toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:99999;padding:12px 24px;border-radius:12px;font-size:14px;font-weight:500;color:#fff;background:#ef4444;box-shadow:0 4px 12px rgba(0,0,0,0.15);display:flex;align-items:center;gap:8px;max-width:500px;';
          toast.innerHTML = `<svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg><span>${userMessage}</span>`;
          document.body.appendChild(toast);
          setTimeout(() => { try { toast.remove(); } catch {} }, 6000);
        } catch {}
      }

      // Return null instead of throwing — prevents uncaught runtime errors
      return null;
    }

    // Don't log "Session not found" 404 errors as they are expected
    const isAuthError = error?.status === 401 || errorMsg.toLowerCase().includes('not authenticated') || errorMsg.toLowerCase().includes('invalid authentication token');

    if (!(isSessionNotFoundError && isSessionLogsEndpoint) && !isAuthError) {
      console.error('AI Automation API request failed:', error);
    }
    throw error;
  }
};

export const saveOutreachTemplate = async (payload) => {
  return aiAutomationRequest("/outreach/templates", {
    method: "POST",
    body: payload,
  });
};

export const getOutreachTemplates = async (cid = null) => {
  const endpoint = cid != null && String(cid).trim() !== ""
    ? `/outreach/templates?cid=${encodeURIComponent(String(cid))}`
    : "/outreach/templates";
  return aiAutomationRequest(endpoint, {
    method: "GET",
  });
};

export const getWorkflowSnapshot = async (sessionId, cid) => {
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedCid = cid != null && String(cid).trim() !== '' ? String(cid).trim() : '';

  if (!normalizedSessionId || !normalizedCid) {
    return null;
  }

  return aiAutomationRequest(
    `/workflows/session/${encodeURIComponent(normalizedSessionId)}/snapshot?cid=${encodeURIComponent(normalizedCid)}`,
    { method: 'GET' },
  );
};

/**
 * Convenience methods for common HTTP methods
 */
export const aiAutomationApi = {
  get: (endpoint, options = {}) => 
    aiAutomationRequest(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, body, options = {}) => 
    aiAutomationRequest(endpoint, { ...options, method: 'POST', body }),
  
  put: (endpoint, body, options = {}) => 
    aiAutomationRequest(endpoint, { ...options, method: 'PUT', body }),
  
  patch: (endpoint, body, options = {}) => 
    aiAutomationRequest(endpoint, { ...options, method: 'PATCH', body }),
  
  delete: (endpoint, options = {}) => 
    aiAutomationRequest(endpoint, { ...options, method: 'DELETE' }),

  /**
   * Upload a FormData payload (e.g. file upload) without body encryption.
   * Auth token and verification token are still included as headers.
   */
  postForm: async (endpoint, formData) => {
    const normalizedBase = resolveAiAutomationBaseUrl();
    const url = `${normalizedBase}${endpoint}`;
    const verificationToken = createVerificationToken();
    const authToken = getAuthToken();
    const headers = {};
    if (verificationToken) headers['X-Verification-Token'] = verificationToken;
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      headers['token'] = authToken;
    }
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      let detail = errText;
      try { detail = JSON.parse(errText)?.detail || errText; } catch {}
      throw new Error(detail || `HTTP ${response.status}`);
    }
    const text = await response.text();
    try { return JSON.parse(text); } catch { return text; }
  },

  /**
   * GET a binary file (e.g. an .xlsx template) with auth headers; returns a Blob.
   * The normal `get` decrypts the response as JSON, so it can't be used for file downloads.
   */
  downloadBlob: async (endpoint) => {
    const normalizedBase = resolveAiAutomationBaseUrl();
    const url = `${normalizedBase}${endpoint}`;
    const verificationToken = createVerificationToken();
    const authToken = getAuthToken();
    const headers = {};
    if (verificationToken) headers['X-Verification-Token'] = verificationToken;
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      headers['token'] = authToken;
    }
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(errText || `HTTP ${response.status}`);
    }
    return response.blob();
  },

  getWorkflowSnapshot,
};

export default aiAutomationApi;
